import React, { useCallback, useEffect, useState } from "react";
import { View, ScrollView, ActivityIndicator, StyleSheet } from "react-native";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import Text from "@components/ds/DsText";
import { t } from "@constants/localization";
import { getFreeRacks, resolveRack } from "@services/api/receiving";
import NativeScannerSheet from "@components/ds/NativeScannerSheet";
import ScanInput from "../components/ScanInput";
import FreeRackRow from "../components/FreeRackRow";
import BottomButton from "@components/ds/BottomButton";
import TpSummaryCard from "../components/TpSummaryCard";
import { showReceivingError } from "../showError";
import {
  getCardDateText,
  getRackRefusal,
  getTpTitleParts,
  isAfterToday,
  shortDate,
} from "../helpers";
import { tokens, sharedStyles } from "@components/ds/tokens";

const PATH = "Receiving/scanRack";
const TP_PATH = "Receiving/tpList";

// Registered as `ReceivingScanRack`. Template: PartnerRoutingAndPacking's
// NativeScannerSheet ({visible,onClose,onDecode,flow}) for the camera.
// Layout matches the design mock (spec p2 frame 3): TP summary card, step
// heading + helper copy, one scan input, wrapping free-rack chips, a
// full-width CTA.

const ReceivingScanRack = ({ navigation, route }) => {
  const { cx_id, date, cx_name, area, shipment_count } = route.params || {};

  const [freeRacks, setFreeRacks] = useState([]);
  const [loadingFreeRacks, setLoadingFreeRacks] = useState(false);
  const [selectedRack, setSelectedRack] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [scannerVisible, setScannerVisible] = useState(false);
  // An occupied rack (mockup §9 frame 05): { code, rackNo, heldBy,
  // suggestedRacks }, shown until another rack is picked.
  const [refusal, setRefusal] = useState(null);

  const loadFreeRacks = useCallback(async () => {
    try {
      setLoadingFreeRacks(true);
      const { data } = await getFreeRacks();
      setFreeRacks(data?.data?.racks || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFreeRacks(false);
    }
  }, []);

  useEffect(() => {
    loadFreeRacks();
  }, [loadFreeRacks]);

  const handleResolve = async (code) => {
    if (!code) return;
    try {
      setResolving(true);
      setRefusal(null);
      const response = await resolveRack({ code, cx_id, date });
      const rack = response?.data?.data?.rack;
      if (rack) {
        setSelectedRack(rack);
      } else {
        // The shared interceptor (src/services/api-utils.js) resolves a 404
        // instead of rejecting it (only 400/401/412/418/429 reject) — a
        // RACK_NOT_FOUND response lands here as an empty `response`, not in
        // the catch below. See errors.js header comment.
        showReceivingError();
      }
    } catch (error) {
      console.error(error);
      const occupied = getRackRefusal(error);
      if (occupied) {
        setSelectedRack(null);
        setRefusal(occupied);
      } else {
        showReceivingError(error);
      }
    } finally {
      setResolving(false);
    }
  };

  const onSelectFreeRack = (rack) => {
    setRefusal(null);
    setSelectedRack(rack);
  };

  const onScanDecode = (decoded) => {
    setScannerVisible(false);
    handleResolve(decoded);
  };

  const onPressContinue = () => {
    if (!selectedRack) return;
    // Replace (not push) so the stack stays List → ScanShipments even after
    // a "+ Add another rack" round-trip through this screen — the back
    // button from ScanShipments should return to the list, not to an
    // already-answered rack picker.
    navigation.replace("ReceivingScanShipments", {
      cx_id,
      date,
      cx_name,
      area,
      rack: {
        rack_id: selectedRack.rack_id,
        rack_no: selectedRack.rack_no,
        rack_name: selectedRack.rack_name,
      },
    });
  };

  const { hasName, name } = getTpTitleParts({ cx_name });
  const tpMeta = hasName
    ? t(`${TP_PATH}/rowMetaWithId`, {
        cx_id,
        count: String(shipment_count ?? ""),
        area: area || "",
      })
    : t(`${TP_PATH}/rowMeta`, {
        count: String(shipment_count ?? ""),
        area: area || "",
      });
  const dateText = getCardDateText(date, t);
  const metaText = dateText ? `${tpMeta} · ${dateText}` : tpMeta;

  return (
    <View style={sharedStyles.screen}>
      <HeaderWithBackAndTitle
        headertitle={hasName ? name : t(`${TP_PATH}/tpLabel`, { cx_id })}
        headerColor={tokens.surface}
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        style={sharedStyles.flex1}
      >
        <TpSummaryCard
          testID="receiving-scan-rack-tp-card"
          titleText={hasName ? name : t(`${TP_PATH}/tpLabel`, { cx_id })}
          metaText={metaText}
        />

        <View style={styles.stepBlock}>
          <Text variant="title_16" style={styles.stepTitle}>
            {t(`${PATH}/stepTitle`)}
          </Text>
          <Text variant="caption_2" style={styles.stepHelper}>
            {t(`${PATH}/stepHelper`)}
          </Text>
        </View>

        <ScanInput
          placeholder={t(`${PATH}/inputPlaceholder`)}
          onManualSubmit={handleResolve}
          onScanPress={() => setScannerVisible(true)}
          disabled={resolving}
          testIDPrefix="receiving-scan-rack"
        />

        {resolving ? (
          <Text variant="caption_2" style={styles.resolving}>
            {t(`${PATH}/resolving`)}
          </Text>
        ) : null}

        {refusal ? (
          <View style={styles.occupied} testID="receiving-rack-occupied">
            <Text variant="title_16" style={styles.occupiedTitle}>
              {t("Receiving/occupied/title", { rack_no: refusal.rackNo })}
            </Text>
            {refusal.heldBy ? (
              <Text variant="body_text_1" style={styles.occupiedHeld}>
                {t("Receiving/occupied/heldBy", {
                  cx_id: refusal.heldBy.cx_id,
                  edd: shortDate(refusal.heldBy.edd),
                }) +
                  (isAfterToday(refusal.heldBy.edd)
                    ? t("Receiving/occupied/advanceSuffix")
                    : "")}
              </Text>
            ) : null}
            <Text variant="caption_2" style={styles.occupiedBody}>
              {t("Receiving/occupied/body")}
            </Text>
            {refusal.suggestedRacks.length ? (
              <>
                <Text variant="caption_2" style={styles.sectionTitleTight}>
                  {t("Receiving/occupied/suggested")}
                </Text>
                <View style={styles.chipWrapTight}>
                  {refusal.suggestedRacks.map((rack) => (
                    <FreeRackRow
                      key={rack.rack_id}
                      rack={rack}
                      selected={false}
                      onPress={onSelectFreeRack}
                    />
                  ))}
                </View>
              </>
            ) : null}
          </View>
        ) : null}

        <Text variant="caption_2" style={styles.sectionTitle}>
          {t(`${PATH}/freeRacksTitle`)}
        </Text>

        {loadingFreeRacks ? (
          <ActivityIndicator color={tokens.primary} style={styles.loading} />
        ) : freeRacks.length === 0 ? (
          <Text variant="caption_2" style={styles.emptyText}>
            {t(`${PATH}/freeRacksEmpty`)}
          </Text>
        ) : (
          <View style={styles.chipWrap} testID="receiving-free-racks-list">
            {freeRacks.map((rack) => (
              <FreeRackRow
                key={rack.rack_id}
                rack={rack}
                selected={selectedRack?.rack_id === rack.rack_id}
                onPress={onSelectFreeRack}
              />
            ))}
          </View>
        )}
      </ScrollView>

      <BottomButton
        title={t(`${PATH}/cta`)}
        disabled={!selectedRack}
        onPress={onPressContinue}
        testID="receiving-scan-rack-cta"
      />

      <NativeScannerSheet
        flow="receiving_rack"
        visible={scannerVisible}
        onClose={() => setScannerVisible(false)}
        onDecode={onScanDecode}
      />
    </View>
  );
};

export default ReceivingScanRack;

const styles = StyleSheet.create({
  stepBlock: {
    paddingHorizontal: tokens.spacing.large,
    paddingBottom: tokens.spacing.small,
  },
  stepTitle: { color: tokens.textPrimary },
  stepHelper: { color: tokens.textSecondary, marginTop: 4 },
  resolving: {
    marginHorizontal: tokens.spacing.large,
    color: tokens.textTertiary,
  },
  sectionTitle: {
    paddingHorizontal: tokens.spacing.large,
    paddingTop: tokens.spacing.small,
    paddingBottom: tokens.spacing.small,
    color: tokens.textTertiary,
  },
  chipWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingHorizontal: tokens.spacing.large,
  },
  loading: { marginTop: tokens.spacing.large },
  occupied: {
    marginHorizontal: tokens.spacing.large,
    marginBottom: tokens.spacing.medium,
    padding: tokens.spacing.large,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: tokens.warningBorder,
    backgroundColor: tokens.warningBg,
  },
  occupiedTitle: { color: tokens.textPrimary },
  occupiedHeld: { color: tokens.textHeading, marginTop: 6 },
  occupiedBody: { color: tokens.warningText, marginTop: 6 },
  sectionTitleTight: {
    color: tokens.textTertiary,
    marginTop: tokens.spacing.medium,
    marginBottom: tokens.spacing.small,
  },
  chipWrapTight: { flexDirection: "row", flexWrap: "wrap" },
  emptyText: {
    textAlign: "center",
    color: tokens.textTertiary,
    marginTop: tokens.spacing.large,
  },
});
